<template>
  <div class="scale">
    <div class="display">
      <PianoKeyboard
        :class="pianoClass"
        :note="note"
        :notes="notes"
        :pcset="pcset"
        :chord-type="chordType"
        :min-oct="3"
        :max-oct="4"
      />
      <ChromaticWheel
        :class="rotoClass"
        :note="note"
        :notes="notesOct"
        :names="names"
        :pcset="pcset"
        :min-oct="3"
        :max-oct="3"
      />
    </div>
    <div class="key-group">
      <div class="row">
        <div class="col expand">
          <div class="key-box">
            <button
              class="btn"
              @click="player()"
            >
              ♫ Play
            </button>
            <h2
              class="key-name"
            >
              {{ beauty(scaleName) }} [{{ alterations }}]
            </h2>
            <span
              v-for="(item, ind) in alteredNotes"
              :key="ind"
              :data-item="item"
              class="item-sup"
            >
              {{ item }}
            </span>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <router-link
            v-for="(item, name) in presets"
            :key="name"
            :to="`/scale/${urlTonic}/${name}`"
            :data-item="item"
            class="preset-item"
          >
            {{ name }}
          </router-link>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <h3
            class="chords-title"
          >
            Chords [7 <sup>th</sup>]
          </h3>
        </div>
      </div>
      <div class="row-flex">
        <div
          v-for="(item, ind) in chords"
          :key="ind"
          :data-item="item"
          class="piano-chord"
          @click="player(item.name, item.note.letter)"
        >
          <div class="chord-name">
            {{ degrees[ind] }}
          </div>
          <div class="scale-name">
            <div
              v-if="relatives[ind]"
              :key="`${relatives[ind].name}`"
              :data-item="relatives[ind].name"
            >
              {{ relatives[ind].name }}
            </div>
          </div>
          <PianoKeyboard
            :note="item.note"
            :notes="item.notes"
            :pcset="item.pcset"
            :chord-type="getChord([`${item}`])[0].chordType"
            :min-oct="3"
            :max-oct="4"
            class="chord-keyboard"
          />
          <div
            class="chord-info"
          >
            <div
              class="chord-name"
            >
              {{ item.chord }}
            </div>
            <div
              class="chord-intervals"
            >
              <span
                v-for="(interval, i) in item.intervals"
                :id="`interval-${i}`"
                :key="interval"
                class="chord-interval"
              >
                {{ interval }}&nbsp;
              </span>
            </div>
          </div>
        </div>
      </div>
      <h3
        class="chords-title"
      >
        Available Chords
      </h3>
      <div class="row-flex wrap">
        <div
          v-for="(item, ind) in allChords"
          :key="ind"
          :data-item="item"
          class="item-chord"
          @click="player(item)"
        >
          <div
            class="chord-name"
          >
            {{ note.pc }}{{ item }}
          </div>
          <PianoKeyboard
            :note="getChord([`${item}`])[0].note"
            :notes="getChord([`${item}`])[0].notes"
            :pcset="getChord([`${item}`])[0].pcset"
            :chord-type="getChord([`${item}`])[0].chordType"
            :min-oct="3"
            :max-oct="4"
            class="chord-keyboard"
          />
          <div
            class="chord-intervals"
          >
            <span
              v-for="(interval, i) in getChord([`${item}`])[0].intervals"
              :id="`interval-${i}`"
              :key="interval"
              class="chord-interval"
            >
              {{ interval }}&nbsp;
            </span>
          </div>
        </div>
      </div>
      <div class="row-flex">
        <div class="col">
          <h3
            class="chords-title"
          >
            General MIDI
          </h3>
        </div>
      </div>
      <div class="row-flex">
        <div class="col">
          <select
            v-model="instrument"
            @change="changeInstrument()"
          >
            <option
              v-for="(item, ind) in instruments"
              :id="`instrument-${ind}`"
              :key="ind"
            >
              <!--@bind:value="instrument"-->
              {{ item }}
            </option>
          </select>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

// import anime from 'animejs's
// import color from 'onecolor'
import { Array, PcSet, Scale, Note, Chord } from 'tonal'
import * as Key from 'tonal-key'
import * as Range from 'tonal-range'
import { transpose } from 'tonal-distance'

import { emitter } from '../main'

import PianoKeyboard from '../components/PianoKeyboard.vue'

// RotoKeyboard
// import RotoKeyboard from '@/components/RotoKeyboard.vue'

import ChromaticWheel from '../components/ChromaticWheel.vue'

// Now it´s planned to be dynamic
// import colorList from '@/data/werner'

import { polarToCartesian } from '../util'

import Soundfont from 'soundfont-player'

import { instruments, buildScale, buildChord } from '../player'

const presets = {
  major: ['Maj7', 'm7', 'm7', 'Maj7', '7', 'm7', 'm7b5'],
  dorian: ['m7', 'm7', 'Maj7', '7', 'm7', 'm7b5', 'Maj7'],
  phrygian: ['m7', 'Maj7', '7', 'm7', 'm7b5', 'Maj7', 'm7'],
  lydian: ['Maj7', '7', 'm7', 'm7b5', 'Maj7', 'm7', 'm7'],
  mixolydian: ['7', 'm7', 'm7b5', 'Maj7', 'm7', 'm7', 'Maj7'],
  aeolian: ['m7', 'm7b5', 'Maj7', 'm7', 'm7', 'Maj7', '7'],
  locrian: ['m7b5', 'Maj7', 'm7', 'm7', 'Maj7', '7', 'm7'],
}

/*
let presetsAll = {
  'major': ['Maj7', 'm7', 'm7', 'Maj7', '7', 'm7', 'm7b5'],
  'dorian': ['m7', 'm7', 'Maj7', '7', 'm7', 'm7b5', 'Maj7'],
  'phrygian': ['m7', 'Maj7', '7', 'm7', 'm7b5', 'Maj7', 'm7'],
  'lydian': ['Maj7', '7', 'm7', 'm7b5', 'Maj7', 'm7', 'm7'],
  'mixolydian': ['7', 'm7', 'm7b5', 'Maj7', 'm7', 'm7', 'Maj7'],
  'minor': ['m7', 'm7b5', 'Maj7', 'm7', 'm7', 'Maj7', '7'],
  'locrian': ['m7b5', 'Maj7', 'm7', 'm7', 'Maj7', '7', 'm7'],
  'melodic minor': ['mMaj7', 'm7', 'M7#5', '7', '7', 'm7b5', 'm7b5'],
  'harmonic minor': ['mMaj7', 'm7b5', 'M7#5', 'm7', '7', 'Maj7', 'o7'],
  'harmonic major': ['Maj7', 'm7b5', 'm7', 'mMaj7', '7', 'M7#5', 'o7'],
}
*/

const ac = new AudioContext()

interface ScaleSet {
  tonic: string
  palette: object
  pianoClass: string
  rotoClass: string
  initial: string
  mode: string
  chordType: string,
  piano: null | any,
  instruments: string[]
  instrument: string
}

export default defineComponent({
  name: 'Scale',
  components: {
    ChromaticWheel,
    PianoKeyboard,
  },
  data () {
    return {
      tonic: '',
      palette: {},
      pianoClass: 'piano-keyboard',
      rotoClass: 'roto-keyboard',
      initial: 'C3',
      mode: 'major',
      chordType: '',
      piano: null,
      instruments: instruments(),
      instrument: instruments()[2],
    } as ScaleSet
  },
  computed: {
    presets (): any {
      return presets
    },
    midi (): any {
      return Note.midi(this.tonic)
    },
    pc (): any {
      return Note.pc(this.tonic)
    },
    oct (): any {
      return Note.oct(this.tonic)
    },
    note (): any {
      return Note.props(this.tonic)
    },
    pcset (): any {
      return {
        tonic: Note.chroma(this.tonic),
        chroma: PcSet.chroma(this.notes) || '',
      }
    },
    enharmonic (): any {
      return Note.enharmonic(this.tonic)
    },
    keyProps (): any {
      const tonic = Note.pc(this.tonic)
      let key
      if (this.mode.search('major') !== -1) key = 'major'
      if (this.mode.search('minor') !== -1) key = 'minor'
      const keyName = tonic + ' ' + this.mode
      this.logMe('keyName', keyName)
      this.logMe('key', key)
      this.logMe('mode', this.mode)
      return Key.props(keyName)
    },
    keyName (): any {
      return this.keyProps.name
    },
    keyChords (): any {
      return Key.chords(this.keyName)
    },
    allChords (): any {
      let scaleChord = Scale.chords(this.mode)
      scaleChord = this.removeFromArray(scaleChord, '5')
      scaleChord = this.removeFromArray(scaleChord, '4')
      scaleChord = this.removeFromArray(scaleChord, '69#11')
      return scaleChord
    },
    chords (): any {
      return this.getChord(this.presets[this.mode])
    },
    degrees (): any {
      return Key.degrees(this.keyName)
    },
    secDomChords (): any {
      return Key.secDomChords(this.keyName)
    },
    triads (): any {
      return Key.triads(this.keyName, [1, 2, 3, 4, 5, 6, 7])
    },
    relative (): any {
      let mode = ''
      if (this.mode === 'major') mode = 'minor'
      return Key.relative(mode, this.keyName)
    },
    intervals (): any {
      return Scale.intervals(this.mode)
    },
    alterations (): any {
      const alt = this.keyProps.alt
      const num = Math.abs(this.keyProps.alt)
      return alt > 0 ? num + '♯' : num + '♭'
    },
    alteredNotes (): any {
      return Key.alteredNotes(this.keyName)
    },
    altType (): any {
      const alt = this.keyProps.alt
      return alt > 0 ? '#' : 'b'
    },
    scale (): any {
      return Scale.notes(this.pc, this.mode)
    },
    midiScaleOct (): any {
      const ms = this.scale
      return ms.map((note: any) => {
        return Note.midi(note + this.oct)
      })
    },
    midiScale (): any {
      return this.midiScaleUp(this.scale)
    },
    notes (): any {
      return {
        tonic: this.midi,
        names: this.midiScale,
        rest: this.midiScale.filter((value: any) => {
          return value !== this.midi
        }),
      }
    },
    names (): any {
      return { scale: Note.names(' ' + this.altType) }
    },
    notesOct (): any {
      return {
        tonic: this.midi,
        names: this.midiScaleOct,
        rest: this.midiScaleOct.filter((value: any) => {
          return value !== this.midi
        }),
      }
    },
    scaleName (): any {
      return `${this.pc} ${this.mode}`
    },
    fifths (): any {
      return Range.fifths('C', [0, 11]).map((note: any) => {
        return Note.enharmonic(note)
      })
    },
    urlTonic (): any {
      return this.beauty(this.tonic)
    },
    relatives (): any[] {
      const rels: any[] = []
      // const scaleModes = Scale.modeNames(`${this.pc} ${this.mode}`)
      // this.logMe('scaleModes', scaleModes)
      Scale.modeNames(`${this.pc} ${this.mode}`).forEach((name) => {
        const beautyNote = this.beauty(name[0])
        const item = {
          name: `${name[1]}`,
          url: `${beautyNote}${this.oct}/${name[1]}`,
        }
        rels.push(item)
      })
      return rels
    },
  },
  watch: {
    mode (): any {
      this.$router.push({
        path: `/scale/${this.urlTonic}/${this.mode}`,
      })
    },
    /*
    instrument (): any {
      this.loadPiano(Soundfont, this.instument)
    },
    tonic () {
      this.$router.push({
        path: `/scale/${this.tonic}/${this.mode}`,
      })
    },
    */
    $route (r): any {
      this.getNoteNameAndSet(r.params.n, r.params.m)
    },
  },
  created () {
    emitter.on('tonicChange', (midi: any) => {
      this.handleTonicChange(midi)
    })
    /* OLD Vue2
    this.$root.$on('tonicChange', (midi) => {
      this.handleTonicChange(midi)
    })
    */
    this.getNoteNameAndSet(this.$route.params.n, this.$route.params.m)
    this.loadPiano(Soundfont)
    // this.getNoteNameAndSet(this.$route.query.n, this.$route.query.m)
  },
  mounted () {
    // @ts-ignore
    this.init()
    // OLD Vue2
    // this.$emit('init', this.init())
  },
  methods: {
    async init () {
      // this.getNoteNameAndSet(this.$route.query.n, this.$route.query.m)
      // this.logMe(Scale.modeNames('A harmonic minor'), '')
    },
    handleTonicChange (midi: any): any {
      const tonicTemp = Note.fromMidi(midi)
      const tonic = Note.pc(tonicTemp)
      const keyName = tonic + ' ' + this.mode
      const altNum = parseInt(Key.props(keyName).alt)
      let altType = false
      if (altNum >= 0 && altNum <= 5) {
        altType = true
      }
      if (altNum <= -7) {
        altType = true
      }
      if (altNum >= 7) {
        altType = false
      }
      this.tonic = Note.fromMidi(midi, altType)
      this.$router.push({
        path: `/scale/${this.urlTonic}/${this.mode}`,
      })
    },
    getNoteNameAndSet (note: any, mode: any): void {
      console.log('getNoteNameAndSet', note, mode)
      let subsNote
      if (note) {
        subsNote = note.replace('♯', '#').replace('♭', 'b')
        this.tonic = Note.name(subsNote)
      } else {
        this.tonic = Note.name(this.initial)
      }
      if (mode) {
        this.mode = mode
      }
      this.player()
    },
    logMe (tit: any, msg: any): void {
      console.log(tit, msg)
    },
    getNoteFreq (): number {
      return Math.round(this.note.freq * 100) / 100
    },
    getChord (chordArray: any[]): object {
      // let chords = Key.leadsheetSymbols(this.presets[this.mode])
      return chordArray.map((name, index) => {
        const chordName = this.scale[index] + name
        if (Chord.exists(chordName)) {
          const chordType = Chord.tokenize(chordName)[1]
          const intervals = Chord.props(chordType).intervals
          const chordNoteName = Note.props(Chord.notes(chordName)[0] + this.oct)
          const chordTonic = chordNoteName.midi
          const chordNotes = intervals.map((interval: string): any => {
            // return Note.midi(transpose(chordNoteName.name, interval)) + ''
            if (chordNoteName.name) {
              const transposed = transpose(chordNoteName.name, interval)
              return Note.midi(transposed.toString())
            }
          })
          /*
          let chordNotes = Chord.notes(chordName).map((name, index) => {
            return Note.midi(name + this.oct)
          })
          */
          if (chordNoteName.name) {
            const pcset = {
              tonic: Note.chroma(chordNoteName.name.toString()),
              chroma: PcSet.chroma(chordNotes) || '',
            }
            const chordSet = {
              name: name,
              chord: chordName,
              chordType: chordType,
              note: chordNoteName,
              intervals: intervals,
              pcset: pcset,
              notes: {
                tonic: chordTonic,
                names: chordNotes,
                rest: chordNotes.filter((value: number) => {
                  return value !== this.midi
                }),
              },
            }
            return chordSet
          }
        } else {
          return {}
        }
      })
    },
    beauty (note: any) {
      return note.replace(/#|b/g, (v: any) => {
        if (v === '#') return '♯'
        if (v === 'b') return '♭'
      })
    },
    polarToCart (cX: any, cY: any, r: any, degAng: any) {
      return polarToCartesian(cX, cY, r, degAng)
    },
    midiScaleUp (ms: any) {
      const midiScale: any = []
      ms.map((note: any, index: any, notes: any) => {
        const midiNext = Note.midi(note + this.oct)
        const midiPrev = Note.midi(notes[index - 1] + this.oct)
        // @ts-ignore
        if (midiNext < midiPrev || midiNext < midiScale[index - 1]) {
          // @ts-ignore
          midiScale.push(midiNext + 12)
        } else {
          midiScale.push(midiNext)
        }
      })
      return midiScale
    },
    midiOrderUp (ms: any) {
      return ms.sort((a: any, b: any) => a - b)
    },
    notesOrder (ms: any) {
      return Array.sort(ms)
    },
    player (chordType: string = '', tonic: string = '') {
      // let intervals = Set.intervals(this.mode)
      // let setchroma = PcSet.chroma(intervals)
      // let Set = this.$options.name === 'Scale' ? Scale : Chord
      // let notes = Set.notes(this.tonic, this.mode)
      const chordIntervals = Chord.props(chordType).intervals

      const chordTonic = tonic !== '' ? tonic : this.tonic

      // let chroma = PcSet.chroma(notes)
      if (!this.piano) return
      const notes =
        this.$options.name === undefined
          ? [this.tonic]
          : chordType === ''
            ? buildScale(this.tonic, this.intervals)
            : buildChord(chordTonic, chordIntervals)
      // Convert to events
      const events = notes.map((note: any, i: number) => ({
        time: chordType === '' ? i * 0.5 : 0,
        note,
      }))
      this.piano.stop(ac.currentTime)
      this.piano.schedule(ac.currentTime, events)
    },
    // @ts-ignore
    loadPiano (Soundfont: any, instrument = this.instrument) {
      return Soundfont.instrument(ac, instrument).then((inst: any) => {
        console.log('Piano loaded!')
        this.piano = inst
      })
    },
    changeInstrument (event: any) {
      this.loadPiano(Soundfont, this.instrument)
    },
    removeFromArray (scaleArr: any, itemVal: number | string) {
      const index = scaleArr.indexOf(itemVal)
      if (index > -1) {
        scaleArr.splice(index, 1)
      }
      return scaleArr
    },
  },
})
</script>

<style lang='scss' scoped>
@import '@/mixin.scss';
$a: white;
.display {
  display: flex;
  flex-direction: column;
  @include for-tablet {
    flex-direction: row;
  }
}
.roto-keyboard {
  height: 28vh;
  @include for-tablet {
    height: 38vh;
  }
  display: flex;
  flex-grow: 1;
}
.piano-keyboard {
  padding: 1rem;
  flex-grow: 4;
  height: 22vh;
  @include for-tablet {
    height: 32vh;
  }
  display: flex;
}
.row-flex {
  display: flex;
  flex-direction: column;
  overflow-x: auto;
  &.wrap {
    flex-wrap: wrap;
  }
  @include for-tablet {
    flex-direction: row;
  }
  .col {
    display: flex;
    flex: 1;
    @include for-phone {
      flex-direction: column;
    }
    @include for-tablet {
      flex-direction: row;
    }
    &.expand {
      flex: 2;
    }
  }
}
.chord-keyboard {
  flex-grow: 4;
  height: auto;
  @include for-tablet {
    height: auto;
  }
}
.scale {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: left;
  background-color: $mainbg;
  a {
    color: $a;
    text-decoration: none;
  }
  .key-box {
    text-transform: capitalize;
    font-size: 1.1rem;
    margin-bottom: 1rem;
    // position: relative;
  }
  .key-name {
    display: inline-block;
  }
  .key-group {
    display: flex;
    flex-direction: column;
    color: white;
    padding: 1rem;
    box-sizing: border-box;
    flex-grow: 1;
    .row {
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      overflow-x: auto;
      overflow-y: hidden;
      margin-bottom: 0.5rem;
      .col {
        display: flex;
        flex-direction: column;
        @include for-tablet {
          flex-direction: row;
        }
        flex: 1;
        &.expand {
          flex: 2;
        }
      }
      .head {
        min-width: 12rem;
      }
      .preset-item {
        font-size: 1.2rem;
        text-align: left;
        flex: 1;
      }
      .item-sup {
        font-size: 1rem;
        line-height: 1;
      }
      a,
      span {
        padding-right: 0.2rem;
      }
    }
  }
  .piano-chord,
  .item-chord {
    flex-direction: column;
    box-sizing: border-box;
    width: calc(100% - 0.5rem);
    @include for-tablet {
      width: calc(50% - 0.5rem);
    }
    @include for-desktop {
      width: calc(25% - 0.5rem);
    }
    display: flex;
    margin: 0.4rem 0.25rem 0.2rem 0.25rem;
    padding: 0.3rem;
    //border: 1px solid white;
    border-radius: 0.2rem;
    background-color: $btnBg;
    position: relative;
    &:hover {
      &:before {
        content: '♬';
        position: absolute;
        top: 0.66rem;
        right: 0.3rem;
        opacity: 0.66;
      }
      background-color: transparent;
      cursor: pointer;
    }
    // text-shadow: -.05rem -.05rem rgba($color: #000000, $alpha: 0.6),
    //             .05rem .05rem rgba($color: #000000, $alpha: 0.6);
  }
  .scale-name,
  .chord-name {
    font-size: 1.2rem;
    text-align: center;
    padding: 0.1rem 0.5rem 0.4rem;
  }
  .scale-name {
    text-transform: capitalize;
  }
  .chord-interval {
    font-size: .8rem;
  }
  .big {
    font-size: 4rem;
    font-weight: bold;
  }
  .mid {
    font-size: 2rem;
    font-weight: bold;
    color: white;
  }
  .mini {
    font-size: 1.2rem;
    font-weight: bold;
    color: white;
    opacity: 0.8;
  }
  small {
    opacity: 0.82;
  }
  .chords {
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
  }
  .chords-title {
    line-height: 1.4rem;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    width: 100%;
    border-bottom: 0.2rem solid white;
    text-align: left;
  }
  .preset-item {
    text-transform: capitalize;
    padding: 0.2rem;
    text-align: center;
    border-bottom: 0.2rem solid white;
    &.router-link-exact-active {
      color: $tonic;
      border-bottom: 0.2rem solid $tonic;
      &.router-link-active {
      }
    }
  }
  .btn {
    display: block;
    //float: left;
    background: $tonic;
    color: white;
    border: none;
    border-radius: 2px;
    float: left;
    line-height: 1.6;
    margin-right: 0.5rem;
  }
  select {
    -webkit-appearance: none;
    background: none;
    border: none;
    border-bottom: 0.2rem solid white;
    color: white;
    padding: 0.5rem 0.5rem 0.5rem 0;
    border-radius: 1px;
    &.before {
      content: '>';
      position: absolute;
      left: 0;
      top: 0;
    }
  }
}
</style>
